import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Avatar, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, List, ListItemAvatar, ListItemButton, ListItemText, Stack, Typography } from '@mui/material';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ClearIcon from '@mui/icons-material/Clear';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { FileClient } from '@@api';
import { useApiClient, useHasPermission } from '@@common';
import { Permissions } from '../../../../common/Permissions';

type FileIdInputProps = {
	value?: string | null;
	onValueChange: (value: string | undefined) => void;
	matchType?: string;
	readOnly?: boolean;
};

export default function FileIdInput({ value, onValueChange, matchType, readOnly = false }: FileIdInputProps) {
	const client = useApiClient(FileClient);
	const canReadFiles = useHasPermission(Permissions.FileRead);
	const [open, setOpen] = useState(false);
	const [page, setPage] = useState(0);
	const selected = useQuery({
		queryKey: ['file-id-input', 'selected', value],
		queryFn: ({ signal }) => client.getById(value!, true, signal),
		enabled: !!value && canReadFiles,
		retry: false,
	});
	const files = useQuery({
		queryKey: ['file-id-input', 'files', page],
		queryFn: ({ signal }) => client.search(undefined, undefined, undefined, page, 25, 'Created', false, true, signal),
		enabled: open && canReadFiles && !readOnly,
		retry: false,
	});
	const matches = (contentType?: string) => !matchType || (matchType.endsWith('/*')
		? contentType?.startsWith(matchType.slice(0, -1)) : contentType === matchType);
	const items = (files.data?.items ?? []).filter(file => matches(file.contentType));

	return (
		<Stack spacing={1} onClick={event => event.stopPropagation()} onMouseDown={event => event.stopPropagation()}>
			<Stack direction="row" spacing={1} alignItems="center">
				<Typography variant="body2" sx={{ overflowWrap: 'anywhere', flex: 1 }}>
					{selected.data?.name || value || 'No file selected'}
				</Typography>
				<Button size="small" startIcon={<FolderOpenIcon />} disabled={readOnly || !canReadFiles} onClick={() => { setPage(0); setOpen(true); }}>Choose file</Button>
				{value && <IconButton size="small" aria-label="Clear selected file" disabled={readOnly} onClick={() => onValueChange(undefined)}><ClearIcon /></IconButton>}
			</Stack>
			{!canReadFiles && <Typography variant="caption" color="text.secondary">File Read permission is required to browse files.</Typography>}
			{selected.isError && <Typography variant="caption" color="error">The selected file is unavailable. Choose another file or clear the selection.</Typography>}
			<Dialog open={open && !readOnly && canReadFiles} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Choose {matchType === 'image/*' ? 'an image' : 'a file'}</DialogTitle>
				<DialogContent dividers>
					{files.isFetching && <CircularProgress size={24} aria-label="Loading files" />}
					{files.isError && <Alert severity="error" action={<Button onClick={() => files.refetch()}>Retry</Button>}>Files could not be loaded.</Alert>}
					{!files.isFetching && !files.isError && items.length === 0 && <Typography>No matching files on this page.</Typography>}
					<List>
						{items.map(file => (
							<ListItemButton key={file.id} selected={value === file.id} disabled={files.isFetching || files.isError} onClick={() => { onValueChange(file.id); setOpen(false); }}>
								<ListItemAvatar><Avatar variant="rounded" src={file.contentType?.startsWith('image/') ? file.url : undefined}><InsertDriveFileIcon /></Avatar></ListItemAvatar>
								<ListItemText primary={file.name} secondary={file.id} />
							</ListItemButton>
						))}
					</List>
				</DialogContent>
				<DialogActions>
					<Button disabled={page === 0 || files.isFetching} onClick={() => setPage(current => current - 1)}>Previous</Button>
					<Typography variant="caption">Page {page + 1}</Typography>
					<Button disabled={!files.data?.hasNextPage || files.isFetching || files.isError} onClick={() => setPage(current => current + 1)}>Next</Button>
					<Button onClick={() => setOpen(false)}>Cancel</Button>
				</DialogActions>
			</Dialog>
		</Stack>
	);
}
